import React from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import { createComponent } from 'react-fela'

import {
	__textarea,
	__shadowTextarea,
	__textareaWrapper,
	__heightSyncronizer,
} from './__style'

const ROWS_HEIGHT = 19

const TextAreaShadowView = createComponent(__shadowTextarea, 'textarea', [
	'aria-hidden',
	'defaultValue',
	'innerRef',
	'rows',
	'readOnly',
	'tabIndex',
	'value',
])

const TextAreaView = createComponent(__textarea, 'textarea', [
	'defaultValue',
	'icon',
	'innerRef',
	'label',
	'name',
	'onBlur',
	'onChange',
	'onFocus',
	'rows',
	'type',
	'value',
	'required',
	'readOnly',
	'textareaRef',
	'maxlength',
])

const TextAreaWrapper = createComponent(__textareaWrapper, 'div')

const HeightSyncronizerWrapper = createComponent(__heightSyncronizer, 'div')

class TextArea extends React.Component {
	// the shadow text box which will have the min number of rows specified by user
	shadow = null

	// the shadow text box which will have the single row.
	singlelineShadow = null

	// the actual text box which will be visible to user
	input = null

	// value in the visible text box
	value = null

	state = {
		height: null,
	}

	handleResize = debounce(() => {
		this.syncHeightWithShadow()
	}, 166) // Corresponds to 10 frames at 60 Hz.

	constructor(props, context) {
		super(props, context)

		// <Input> expects the components it renders to respond to 'value'
		// so that it can check whether they are filled.
		this.value = props.value || props.defaultValue || ''
		this.state = {
			height: parseInt(props.rows, 10) * ROWS_HEIGHT,
		}
	}

	componentDidMount() {
		this.syncHeightWithShadow()
	}

	componentDidUpdate() {
		this.syncHeightWithShadow()
	}

	componentWillUnmount() {
		this.handleResize.cancel()
	}

	handleRefInput = node => {
		this.input = node
		if (this.props.textareaRef) {
			this.props.textareaRef(node)
		}
	}

	handleRefSinglelineShadow = node => {
		this.singlelineShadow = node
	}

	handleRefShadow = node => {
		this.shadow = node
	}

	handleChange = event => {
		this.value = event.target.value

		if (typeof this.props.value === 'undefined' && this.shadow) {
			// The component is not controlled, we need to update the shallow value.
			this.shadow.value = this.value
			this.syncHeightWithShadow()
		}

		if (this.props.onChange) {
			this.props.onChange(event)
		}
	}

	syncHeightWithShadow(props = this.props) {
		if (!this.shadow || !this.singlelineShadow) {
			return
		}

		const { value } = this.props

		// The component is controlled, we need to update the shallow value.
		if (typeof value !== 'undefined') {
			this.shadow.value = props.value === null ? '' : String(props.value)
		}

		const lineHeight = this.singlelineShadow.scrollHeight
		let newHeight = this.shadow.scrollHeight

		// Guarding for jsdom, where scrollHeight isn't present.
		// See https://github.com/tmpvar/jsdom/issues/1013
		if (newHeight === undefined) {
			return
		}

		if (parseInt(props.rowsMax, 10) >= parseInt(props.rows, 10)) {
			newHeight = Math.min(
				parseInt(props.rowsMax, 10) * lineHeight,
				newHeight
			)
		}

		newHeight = Math.max(newHeight, lineHeight)

		const { height } = this.state
		if (height !== newHeight) {
			this.setState({
				height: newHeight,
			})
		}
	}

	render() {
		const {
			defaultValue,
			onChange,
			rows,
			rowsMax,
			textareaRef,
			value,
			hasError,
			...other
		} = this.props

		const { height } = this.state

		return (
			<TextAreaWrapper hasError={hasError}>
				<HeightSyncronizerWrapper height={height}>
					<TextAreaShadowView
						aria-hidden="true"
						innerRef={node => this.handleRefSinglelineShadow(node)}
						tabIndex={-1}
						rows="1"
						readOnly
						value=""
					/>
					<TextAreaShadowView
						aria-hidden="true"
						defaultValue={defaultValue}
						innerRef={node => this.handleRefShadow(node)}
						rows={rows}
						readOnly
						tabIndex={-1}
						value={value}
					/>
					<TextAreaView
						aria-multiline="true"
						defaultValue={defaultValue}
						innerRef={node => this.handleRefInput(node)}
						onChange={this.handleChange}
						rows={rows}
						value={value}
						{...other}
					/>
				</HeightSyncronizerWrapper>
			</TextAreaWrapper>
		)
	}
}

TextArea.propTypes = {
	/**
	 * @ignore
	 */
	defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	/**
	 * @ignore
	 */
	disabled: PropTypes.bool,
	/**
	 * @ignore
	 */
	onChange: PropTypes.func,
	/**
	 * Number of rows to display when multiline option is set to true.
	 */
	rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	/**
	 * Maximum number of rows to display when multiline option is set to true.
	 */
	rowsMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	/**
	 * Use that property to pass a ref callback to the native textarea element.
	 */
	textareaRef: PropTypes.func,
	/**
	 * @ignore
	 */
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

TextArea.defaultProps = {
	rows: 1,
}

export default TextArea
